import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { getToday } from "utils/date";
import { getCommaNumber } from "utils/format";
import iconDrop from "assets/icons/icon_drop_black.svg";
import Block from "components/ui/Block";
import BarChart from "components/ui/chart/Bar";
import PopupCalendar from "components/ui/popup/Calendar";
import DatePicker from "components/ui/popup/DatePicker";
import PaymentMethodList from "components/salesPayment/PaymentMethodList";
import VoucherSummaryInfo from "../voucher/SummaryInfo";
import { seriesToChartData } from "components/salesPayment/PaymentSummaryCard";
import type {
  PaymentMethodItem,
  Refund,
  SeriesItem,
  ServiceBreakdown,
} from "types/ManagerPayment";

export interface SummaryData {
  totalAmount: number;
  paymentMethods?: PaymentMethodItem[];
  refund?: Refund;
  series?: SeriesItem[];
  serviceBreakdown?: ServiceBreakdown;
}

interface Props {
  mode: "daily" | "weekly" | "monthly" | "receivable";
  data?: SummaryData;
  color?: string;
  isChart?: boolean;
  isLoading?: boolean;
  isError?: boolean;
  onRefresh?: () => void;
  onChange?: (date: string) => void;
  date?: string;
  title?: string;
}

export const SummaryInfo = ({
  mode,
  data,
  color,
  isChart = true,
  isLoading,
  isError,
  onRefresh,
  onChange,
  ...props
}: Props) => {
  const [date, setDate] = useState<string>(props.date || getToday());

  useEffect(() => {
    if (date && onChange) onChange(date);
  }, [date]);

  useEffect(() => {
    let d = props.date;
    if (!d) {
      switch (mode) {
        case "daily":
          d = (new Date() as any).getCalendar();
          break;
        case "weekly":
          d = (new Date() as any).getStartDateInWeek().getCalendar();
          break;
        case "monthly":
          d = (new Date() as any).getStartDateInMonth().getCalendar();
          break;
      }
    }
    setDate(d as string);
  }, [props.date]);

  const changeDate = (_name: string, value: string) => {
    let d: string = value;
    if (mode === "weekly")
      d = (new Date(value) as any).getStartDateInWeek().getCalendar();
    if (mode === "monthly")
      d = (new Date(value) as any).getStartDateInMonth().getCalendar();
    setDate(d);
  };

  const getDateInfo = () => {
    if (mode === "receivable") return <h4>{props.title}</h4>;
    if (mode === "monthly") {
      const year = new Date(date).getFullYear();
      const month = new Date(date).getMonth() + 1;
      return (
        <DatePicker
          mode="month"
          date={date}
          onChange={(v: string) => setDate(v)}
        >
          <h4 className="icon">
            {year}년 {month}월
          </h4>
        </DatePicker>
      );
    }
    let text = props.title || "-";
    if (mode === "daily") {
      text = (new Date(date) as any).format("sm월 sd일 ww");
    } else if (mode === "weekly") {
      const m = (new Date(date) as any).add(0, 0, 3).getMonth() + 1;
      const weekInMonth = (new Date(date) as any).getWeekInMonth();
      const s = (new Date(date) as any).format("zm.sd(sw)");
      const e = (new Date(date) as any).add(0, 0, 6).format("zm.sd(sw)");
      text = `${m}월 ${weekInMonth}주차 · <span>${s}~${e}</span>`;
    }
    const calDate =
      mode === "weekly"
        ? (new Date(date) as any).add(0, 0, 3).getCalendar()
        : date;
    return (
      <PopupCalendar mode={mode} value={date} date={calDate} onChange={changeDate}>
        <h4
          className="icon"
          dangerouslySetInnerHTML={{ __html: text }}
        />
      </PopupCalendar>
    );
  };

  return (
    <StyledBlock
      className={color || undefined}
      onRefresh={onRefresh}
      isLoading={isLoading}
      isError={isError}
    >
      <div className="dateInfo">
        {getDateInfo()}
        <h2>{getCommaNumber(data?.totalAmount || 0)}원</h2>
      </div>
      {isChart && data?.series?.length ? (
        <div className="chartInfo">
          <BarChart data={seriesToChartData(data.series)} height={150} />
        </div>
      ) : null}
      {data?.serviceBreakdown ? (
        <VoucherSummaryInfo breakdown={data.serviceBreakdown} />
      ) : null}
      {data?.paymentMethods && data?.refund ? (
        <div className="summaryInfo">
          <PaymentMethodList methods={data.paymentMethods} refund={data.refund} />
        </div>
      ) : null}
    </StyledBlock>
  );
};

const StyledBlock = styled(Block)`
  & {
    position: relative;
    padding: 36px 24px;
    background-color: var(--lightBlue);
  }
  &.red {
    background-color: #fbf1f1;
  }
  .dateInfo {
    text-align: center;
  }
  .dateInfo h2 {
    margin-top: 2px;
    font-size: 1.75rem;
  }
  .dateInfo h4 {
    display: inline-flex;
    align-items: center;
    font-weight: 500;
  }
  .dateInfo h4.icon {
    padding-right: 1em;
    background: url(${iconDrop}) no-repeat center right / 0.675em;
  }
  .dateInfo h4 span {
    margin-left: 0.25em;
    font-size: var(--16px);
    font-weight: 400;
    color: var(--darkGray);
  }
  .summaryInfo {
    margin-top: 20px;
    padding-top: 8px;
    border-top: 1px solid var(--border);
  }
  .chartInfo {
    padding-top: 8px;
  }
`;

export default SummaryInfo;
